import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Container, Box, Typography, Button, IconButton } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NearMeIcon from "@mui/icons-material/NearMe";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useLanguage } from "../../contexts/LanguageContext";
import { getServices, getCustomers } from "../../services/storage";
import type { Service, Customer } from "../../types/api.types";
import type { Language } from "../../types/lang.types";

const SERVICE_IMAGES: Record<number, string> = {
  1: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
  2: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?w=800&q=80",
  3: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
  4: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=800&q=80",
  5: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=80",
  6: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
  7: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
};
const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80";

const MotionBox = motion(Box);

// Logo image with initials fallback
const CustomerLogo: React.FC<{
  logo: string;
  initials: string;
  color: string;
}> = ({ logo, initials, color }) => {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [logo]);
  return failed || !logo ? (
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "50%",
        bgcolor: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          color,
          fontSize: "0.9rem",
          letterSpacing: "0.5px",
        }}
      >
        {initials}
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "50%",
        overflow: "hidden",
        bgcolor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={logo}
        alt={initials}
        onError={() => setFailed(true)}
        sx={{ width: 36, height: 36, objectFit: "contain" }}
      />
    </Box>
  );
};

// ─── Industry templates showcase ────────────────────────────────────────────
interface Template {
  id: number;
  industry: string;
  brand: string;
  navItems: string[];
  headline: string;
  subline: string;
  cta: string;
  image: string;
  accent: string;
  navBg: string;
}
const TEMPLATES: Template[] = [
  {
    id: 0,
    industry: "industries.gastro",
    brand: "TOP BHS",
    navItems: [],
    headline: "industries.gastro_headline",
    subline: "industries.gastro_subline",
    cta: "industries.cta",
    image:
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80",
    accent: "#1737bc",
    navBg: "rgba(2,6,20,0.92)",
  },
  {
    id: 1,
    industry: "industries.trade",
    brand: "TOP BHS",
    navItems: [],
    headline: "industries.trade_headline",
    subline: "industries.trade_subline",
    cta: "industries.cta",
    image:
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80",
    accent: "#2a4fd4",
    navBg: "rgba(2,6,20,0.92)",
  },
  {
    id: 2,
    industry: "industries.freelance",
    brand: "TOP BHS",
    navItems: [],
    headline: "industries.freelance_headline",
    subline: "industries.freelance_subline",
    cta: "industries.cta",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80",
    accent: "#4b6ee8",
    navBg: "rgba(2,6,20,0.92)",
  },
  {
    id: 3,
    industry: "industries.crafts",
    brand: "TOP BHS",
    navItems: [],
    headline: "industries.crafts_headline",
    subline: "industries.crafts_subline",
    cta: "industries.cta",
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80",
    accent: "#1737bc",
    navBg: "rgba(2,6,20,0.92)",
  },
  {
    id: 4,
    industry: "industries.medical",
    brand: "TOP BHS",
    navItems: [],
    headline: "industries.medical_headline",
    subline: "industries.medical_subline",
    cta: "industries.cta",
    image:
      "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1200&q=80",
    accent: "#2a4fd4",
    navBg: "rgba(2,6,20,0.92)",
  },
  {
    id: 5,
    industry: "industries.franchise",
    brand: "TOP BHS",
    navItems: [],
    headline: "industries.franchise_headline",
    subline: "industries.franchise_subline",
    cta: "industries.cta",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80",
    accent: "#1737bc",
    navBg: "rgba(2,6,20,0.92)",
  },
  {
    id: 6,
    industry: "industries.apotheke",
    brand: "TOP BHS",
    navItems: [],
    headline: "industries.apotheke_headline",
    subline: "industries.apotheke_subline",
    cta: "industries.cta",
    image:
      "https://images.unsplash.com/photo-1659989693434-727c2b1b8367?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D?w=1000&q=60",
    accent: "#4b6ee8",
    navBg: "rgba(2,6,20,0.92)",
  },
];

const CARD_WIDTH = 380;
const CARD_GAP = 20;
const CARDS_VISIBLE = 3;

// ─── Hero background videos ────────────────────────────────────────────────
// ─── Hero Videos ──────────────────────────────────────────────────────────────
// Kostenlose Buchhaltungs-/Business-Videos von Pexels herunterladen und in
// /public/videos/ speichern. Direkte Download-Links (HD reicht, kein 4K nötig):
//
//  Video 1 (Büro / Menschen bei der Arbeit):
//    https://www.pexels.com/video/3943952/download/
//    → speichern als: /public/videos/office-work.mp4
//
//  Video 2 (Buchhaltung / Dokumente):
//    https://www.pexels.com/video/7578539/download/
//    → speichern als: /public/videos/accounting-docs.mp4
//
//  Video 3 (Business Meeting / Beratung):
//    https://www.pexels.com/video/4491461/download/
//    → speichern als: /public/videos/business-meeting.mp4
//
//  Video 4 (Finanzen / Taschenrechner):
//    https://www.pexels.com/video/3196161/download/
//    → speichern als: /public/videos/finance.mp4
const HERO_VIDEOS = [
  "/videos/3191887-uhd_3840_2160_25fps.mp4",
  "/videos/7792304-hd_1920_1080_25fps.mp4",
  "/videos/8141403-uhd_3840_2160_25fps.mp4",
];
const VIDEO_DURATION_MS = 7000; // how long each clip plays before fading to the next

const HomePage: React.FC = () => {
  const { currentLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [videoIndex, setVideoIndex] = useState(0);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [templateIndex, setTemplateIndex] = useState(0);
  const [tplPaused, setTplPaused] = useState(false);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const advanceVideo = useCallback(() => {
    setVideoIndex((prev) => (prev + 1) % HERO_VIDEOS.length);
  }, []);

  const prevSlide = useCallback(() => {
    setSliderIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const nextSlide = useCallback(() => {
    setSliderIndex((prev) =>
      Math.min(Math.max(0, services.length - CARDS_VISIBLE), prev + 1),
    );
  }, [services.length]);

  useEffect(() => {
    timerRef.current = setTimeout(advanceVideo, VIDEO_DURATION_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [videoIndex, advanceVideo]);

  useEffect(() => {
    getServices()
      .then((list) => setServices(list.filter((s) => s.is_active)))
      .catch(() => {});
    getCustomers()
      .then(setCustomers)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tplPaused) {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
      return;
    }
    autoScrollRef.current = setInterval(() => {
      setTemplateIndex((p) => p + 1);
    }, 5000);
    return () => {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    };
  }, [tplPaused]);

  const titleKey = `title_${currentLanguage}` as `title_${Language}`;
  const descKey = `description_${currentLanguage}` as `description_${Language}`;

  const N = TEMPLATES.length;
  const getTpl = (idx: number): Template => TEMPLATES[((idx % N) + N) % N];
  const activeIdx = ((templateIndex % N) + N) % N;
  const activeTpl = getTpl(templateIndex);
  const goToIndustry = (i: number) => {
    const cur = ((templateIndex % N) + N) % N;
    let diff = i - cur;
    if (diff > N / 2) diff -= N;
    if (diff < -N / 2) diff += N;
    setTemplateIndex((p) => p + diff);
    setTplPaused(false);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: "#3d3d3d",
          minHeight: "92vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background video with crossfade */}
        <AnimatePresence>
          <motion.div
            key={videoIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
            style={{ position: "absolute", inset: 0, zIndex: 0 }}
          >
            <video
              src={HERO_VIDEOS[videoIndex]}
              autoPlay
              muted
              playsInline
              onEnded={advanceVideo}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </motion.div>
        </AnimatePresence>

        {/* Dark overlay so text stays readable */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0, 0, 0, 0.58)",
            zIndex: 1,
          }}
        />

        {/* Video indicator dots */}
        <Box
          sx={{
            position: "absolute",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 1,
            zIndex: 10,
          }}
        >
          {HERO_VIDEOS.map((_, i) => (
            <Box
              key={i}
              onClick={() => setVideoIndex(i)}
              sx={{
                width: i === videoIndex ? 24 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: i === videoIndex ? "white" : "rgba(255,255,255,0.35)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": { bgcolor: "rgba(255,255,255,0.7)" },
              }}
            />
          ))}
        </Box>

        <Container
          maxWidth="lg"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            pb: 8,
            mx: "auto",
          }}
        >
          <Box sx={{ maxWidth: 780 }}>
            <MotionBox
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
            >
              <Typography
                variant="h1"
                sx={{
                  color: "white",
                  mb: 4,
                  fontSize: { xs: "1.4rem", sm: "1.7rem", md: "2.2rem" },
                  fontWeight: 700,
                  letterSpacing: "-1px",
                  lineHeight: 1.15,
                }}
              >
                {t("hero.title")}
              </Typography>
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.35 }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  mb: 6,
                  fontSize: "0.95rem",
                  lineHeight: 1.65,
                  maxWidth: 520,
                }}
              >
                {t("hero.subtitle")}
              </Typography>
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.6 }}
            >
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: "white",
                    color: "#3d3d3d",
                    px: 4,
                    py: 1.5,
                    fontSize: "0.95rem",
                    "&:hover": {
                      bgcolor: "#f0f0f0",
                      transform: "translateY(-1px)",
                    },
                  }}
                  endIcon={<ArrowForwardIcon />}
                >
                  {t("hero.cta_primary")}
                </Button>
                <Button
                  component={RouterLink}
                  to="/services"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "rgba(255,255,255,0.3)",
                    borderWidth: "1.5px",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "0.95rem",
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.8)",
                      borderWidth: "1.5px",
                      bgcolor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  {t("hero.cta_secondary")}
                </Button>
              </Box>
            </MotionBox>
          </Box>
        </Container>
      </Box>

      {/* Stats Strip */}
      <Box sx={{ bgcolor: "#ffffff" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              py: { xs: 4, md: 5 },
              gap: { xs: 4, md: 0 },
            }}
          >
            {[
              { value: "10+", label: "Jahre Erfahrung" },
              { value: "4", label: "Sprachen" },
              { value: "100%", label: "Digital" },
              { value: "Dortmund", label: "Unser Standort" },
            ].map((stat, i, arr) => (
              <MotionBox
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: false }}
                sx={{
                  flex: 1,
                  minWidth: 140,
                  textAlign: "center",
                  px: 3,
                  borderRight:
                    i < arr.length - 1
                      ? { xs: "none", md: "1px solid #ebebeb" }
                      : "none",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-1px",
                    color: "#1a2640",
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                    mt: 0.75,
                    display: "block",
                  }}
                >
                  {stat.label}
                </Typography>
              </MotionBox>
            ))}
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Box sx={{ bgcolor: "#ffffff", py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: { xs: 6, md: 10 },
            }}
          >
            {/* Image */}
            <MotionBox
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              viewport={{ once: false }}
              sx={{ flex: "0 0 auto", width: { xs: "100%", md: "48%" } }}
            >
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  overflow: "hidden",
                  aspectRatio: "4/3",
                  boxShadow: "0 24px 64px rgba(0,0,0,0.12)",
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=900&q=80"
                  alt="TOP BHS Team"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {/* Accent bar */}
                <MotionBox
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
                  viewport={{ once: false }}
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    bgcolor: "#1737bc",
                    transformOrigin: "left",
                  }}
                />
              </Box>
            </MotionBox>

            {/* Text */}
            <MotionBox
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: false }}
              sx={{ flex: 1 }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "text.secondary",
                  letterSpacing: "3px",
                  mb: 2,
                  display: "block",
                }}
              >
                {t("about.label")}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  letterSpacing: "-1px",
                  lineHeight: 1.15,
                  fontSize: { xs: "2rem", md: "2.6rem" },
                }}
              >
                {t("about.title")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.8,
                  fontSize: "1.05rem",
                  mb: 5,
                }}
              >
                {t("about.description")}
              </Typography>

              {/* Highlights */}
              <Box sx={{ display: "flex", gap: 4, mb: 5, flexWrap: "wrap" }}>
                {[
                  {
                    value: t("about.highlight1_value"),
                    label: t("about.highlight1_label"),
                  },
                  {
                    value: t("about.highlight2_value"),
                    label: t("about.highlight2_label"),
                  },
                  {
                    value: t("about.highlight3_value"),
                    label: t("about.highlight3_label"),
                  },
                ].map((h, i) => (
                  <MotionBox
                    key={h.label}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.2 + i * 0.1 }}
                    viewport={{ once: false }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "-1px",
                        lineHeight: 1,
                      }}
                    >
                      {h.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        fontSize: "0.7rem",
                      }}
                    >
                      {h.label}
                    </Typography>
                  </MotionBox>
                ))}
              </Box>

              <MotionBox
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: false }}
                sx={{ display: "inline-block" }}
              >
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: "#1737bc",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      bgcolor: "#0f2899",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {t("about.cta")}
                </Button>
              </MotionBox>
            </MotionBox>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: "#ffffff", py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 8, md: 10 } }}>
            <MotionBox
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: false }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "#1737bc",
                  letterSpacing: "3px",
                  mb: 2,
                  display: "block",
                  fontSize: "0.75rem",
                }}
              >
                {t("benefits.label")}
              </Typography>
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: false }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-1px",
                  lineHeight: 1.15,
                  fontSize: { xs: "2rem", md: "2.8rem" },
                }}
              >
                {t("benefits.title")}
              </Typography>
            </MotionBox>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 4,
            }}
          >
            {[
              {
                icon: <AccessTimeIcon sx={{ fontSize: 36, color: "white" }} />,
                title: t("benefits.item1_title"),
                desc: t("benefits.item1_desc"),
                image:
                  "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=700&q=80",
              },
              {
                icon: (
                  <SavingsOutlinedIcon sx={{ fontSize: 36, color: "white" }} />
                ),
                title: t("benefits.item2_title"),
                desc: t("benefits.item2_desc"),
                image:
                  "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=700&q=80",
              },
              {
                icon: (
                  <LockOutlinedIcon sx={{ fontSize: 36, color: "white" }} />
                ),
                title: t("benefits.item3_title"),
                desc: t("benefits.item3_desc"),
                image:
                  "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=700&q=80",
              },
            ].map((item, i) => (
              <MotionBox
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.55, delay: i * 0.12 }}
                viewport={{ once: false }}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative",
                  height: 380,
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  border: "1px solid rgba(255,255,255,0.08)",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.02)" },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(160deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.78) 100%)",
                  }}
                />
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    p: { xs: 4, md: 5 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                  }}
                >
                  <Box sx={{ mb: 2 }}>{item.icon}</Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      mb: 1.5,
                      letterSpacing: "-0.3px",
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      lineHeight: 1.75,
                      fontSize: "0.95rem",
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </MotionBox>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Benefits → Templates */}
      <Box
        sx={{
          height: 220,
          background: [
            "linear-gradient(to bottom, #ffffff 0%, rgba(255,255,255,0) 100%)",
            "linear-gradient(to right, #f0f8ff 0%, #c8e4f8 20%, #6ab2e8 50%, #2a7bcb 75%, #1a56b0 100%)",
          ].join(", "),
          flexShrink: 0,
        }}
      />

      {/* Templates Showcase Section */}
      <Box
        sx={{
          py: { xs: 8, md: 14 },
          overflow: "hidden",
          background:
            "linear-gradient(to right, #f0f8ff 0%, #c8e4f8 20%, #6ab2e8 50%, #2a7bcb 75%, #1a56b0 100%)",
        }}
        onMouseEnter={() => setTplPaused(true)}
        onMouseLeave={() => setTplPaused(false)}
      >
        {/* Header */}
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            sx={{ textAlign: "center", mb: { xs: 6, md: 10 } }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "rgba(255,255,255,0.35)",
                letterSpacing: "4px",
                mb: 2,
                display: "block",
                fontSize: "0.7rem",
              }}
            >
              {t("templates.label")}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: "white",
                fontWeight: 700,
                letterSpacing: "-1.5px",
                mb: 2.5,
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              {t("templates.title")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(41, 29, 29, 0.76)",
                fontSize: "1rem",
                maxWidth: 480,
                mx: "auto",
              }}
            >
              {t("templates.subtitle")}
            </Typography>
          </MotionBox>
        </Container>

        {/* 3-Card Showcase — infinite loop via virtual positions */}
        <Box
          sx={{
            position: "relative",
            height: { xs: 300, md: 540 },
            overflow: "hidden",
          }}
        >
          {[-2, -1, 0, 1, 2].map((offset) => {
            const pos = templateIndex + offset;
            const tpl = getTpl(pos);
            const isCenter = offset === 0;
            const isVisible = Math.abs(offset) <= 1;
            return (
              <Box
                key={pos}
                onClick={() => {
                  if (!isCenter) {
                    setTemplateIndex(pos);
                    setTplPaused(false);
                  }
                }}
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: { xs: 290, md: 760 },
                  height: { xs: 230, md: 460 },
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: isCenter
                    ? `1.5px solid ${activeTpl.accent}`
                    : "1px solid rgba(255,255,255,0.07)",
                  boxShadow: isCenter
                    ? `0 0 0 1px ${activeTpl.accent}33, 0 40px 100px rgba(0,0,0,0.8)`
                    : "none",
                  transform: {
                    xs: `translate(calc(-50% + ${offset * 310}px), -50%) scale(${isCenter ? 1 : 0.8})`,
                    md: `translate(calc(-50% + ${offset * 820}px), -50%) scale(${isCenter ? 1 : 0.83})`,
                  },
                  transition:
                    "transform 1.1s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.8s ease, box-shadow 0.6s ease, border-color 0.6s ease",
                  opacity: isVisible ? (isCenter ? 1 : 0.38) : 0,
                  zIndex: isCenter ? 3 : 1,
                  cursor: isCenter ? "default" : "pointer",
                  pointerEvents: isVisible ? "auto" : "none",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Photo */}
                <Box
                  sx={{
                    flex: 1,
                    position: "relative",
                    backgroundImage: `url(${tpl.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(160deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.5) 100%)",
                    }}
                  />
                  {/* Industry name badge — top left */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: { xs: 10, md: 24 },
                      left: { xs: 10, md: 28 },
                      zIndex: 2,
                      bgcolor: "rgba(0,0,0,0.55)",
                      backdropFilter: "blur(6px)",
                      border: `1px solid ${tpl.accent}66`,
                      borderRadius: "6px",
                      px: { xs: 1, md: 1.8 },
                      py: { xs: 0.3, md: 0.6 },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "white",
                        fontSize: { xs: "0.6rem", md: "0.78rem" },
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {t(tpl.industry)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: { xs: 16, md: 44 },
                      left: { xs: 14, md: 40 },
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "white",
                        fontWeight: 900,
                        fontSize: { xs: "1rem", md: "2.6rem" },
                        lineHeight: 1.1,
                        letterSpacing: { xs: 0, md: "-1px" },
                        whiteSpace: "pre-line",
                        mb: { xs: 0.5, md: 2 },
                        textShadow: "0 2px 20px rgba(0,0,0,0.5)",
                      }}
                    >
                      {t(tpl.headline)}
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.55)",
                        fontSize: "0.9rem",
                        display: { xs: "none", md: "block" },
                        mb: 2.5,
                      }}
                    >
                      {t(tpl.subline)}
                    </Typography>
                    {/* Mini CTA inside hero */}
                    <Box
                      component={RouterLink}
                      to="/contact"
                      sx={{
                        display: { xs: "none", md: "inline-flex" },
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "white",
                        color: "#111",
                        px: 2.5,
                        py: 1,
                        borderRadius: 1,
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        textDecoration: "none",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#f0f0f0" },
                      }}
                    >
                      {t(tpl.cta)}
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Industry tabs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
            mt: 6,
            px: 3,
          }}
        >
          {TEMPLATES.map((tpl, i) => (
            <Box
              key={tpl.id}
              onClick={() => goToIndustry(i)}
              sx={{
                px: 2.5,
                py: 0.75,
                borderRadius: 10,
                cursor: "pointer",
                fontSize: "0.78rem",
                fontWeight: i === activeIdx ? 700 : 400,
                color: i === activeIdx ? "#111" : "rgba(22, 14, 14, 0.75)",
                bgcolor: i === activeIdx ? activeTpl.accent : "transparent",
                border: `1px solid ${i === activeIdx ? activeTpl.accent : "rgba(22, 14, 14, 0.75)"}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  color: "white",
                  borderColor: "rgba(255,255,255,0.4)",
                },
              }}
            >
              {t(tpl.industry)}
            </Box>
          ))}
        </Box>

        {/* CTA */}
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: "white",
              color: "#111",
              px: 4.5,
              py: 1.6,
              borderRadius: 1.5,
              fontWeight: 700,
              "&:hover": {
                bgcolor: "#f0f0f0",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(255,255,255,0.15)",
              },
              transition: "all 0.25s ease",
            }}
          >
            {t("footer.consultation_cta")}
          </Button>
        </Box>
      </Box>

      {/* Templates → Services */}
      <Box
        sx={{
          height: 250,
          background: [
            "linear-gradient(to bottom, rgba(245,245,245,0) 0%, #f5f5f5 100%)",
            "linear-gradient(to right, #f0f8ff 0%, #c8e4f8 20%, #6ab2e8 50%, #2a7bcb 75%, #1a56b0 100%)",
          ].join(", "),
          flexShrink: 0,
        }}
      />

      {/* Services Slider Section */}
      <Box sx={{ bgcolor: "#f5f5f5", overflow: "hidden" }}>
        {/* Header row with arrows */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: false }}
          sx={{
            px: { xs: 3, md: 6 },
            mb: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "#3d3d3d",
                fontWeight: 700,
                fontSize: { xs: "1.6rem", md: "2.2rem" },
                letterSpacing: "-0.5px",
              }}
            >
              {t("services.subtitle")}
            </Typography>
            <Box
              component={RouterLink}
              to="/services"
              sx={{
                color: "rgba(0,0,0,0.45)",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.9rem",
                whiteSpace: "nowrap",
                "&:hover": { color: "rgba(0,0,0,0.75)" },
                transition: "color 0.2s",
              }}
            >
              {t("common.all_services")}
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={prevSlide}
              disabled={sliderIndex === 0}
              sx={{
                border: "1px solid rgba(0,0,0,0.2)",
                color: "#3d3d3d",
                "&:hover": {
                  border: "1px solid rgba(0,0,0,0.5)",
                  bgcolor: "rgba(0,0,0,0.06)",
                },
                "&.Mui-disabled": {
                  border: "1px solid rgba(0,0,0,0.08)",
                  color: "rgba(0,0,0,0.2)",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={nextSlide}
              disabled={sliderIndex >= services.length - CARDS_VISIBLE}
              sx={{
                border: "1px solid rgba(0,0,0,0.2)",
                color: "#3d3d3d",
                "&:hover": {
                  border: "1px solid rgba(0,0,0,0.5)",
                  bgcolor: "rgba(0,0,0,0.06)",
                },
                "&.Mui-disabled": {
                  border: "1px solid rgba(0,0,0,0.08)",
                  color: "rgba(0,0,0,0.2)",
                },
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </MotionBox>
        {/* Slider track */}
        <Box sx={{ position: "relative" }}>
          <Box sx={{ pl: { xs: 3, md: 6 }, overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                gap: `${CARD_GAP}px`,
                transition:
                  "transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                transform: `translateX(calc(-${sliderIndex} * (${CARD_WIDTH}px + ${CARD_GAP}px)))`,
              }}
            >
              {services.map((service) => (
                <Box
                  key={service.id}
                  onClick={() => navigate(`/services/${service.id}`)}
                  sx={{
                    flexShrink: 0,
                    width: CARD_WIDTH,
                    height: 520,
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative",
                    cursor: "pointer",
                    backgroundImage: `url(${SERVICE_IMAGES[service.order_index] ?? FALLBACK_IMAGE})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      "& .card-blur-overlay": { opacity: 1 },
                      "& .card-hover-info": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                      "& .card-default-content": { opacity: 0 },
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(160deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.72) 100%)",
                      zIndex: 1,
                    },
                  }}
                >
                  {/* Default content (visible by default) */}
                  <Box
                    className="card-default-content"
                    sx={{
                      transition: "opacity 0.3s ease",
                      zIndex: 2,
                      position: "absolute",
                      inset: 0,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: 16,
                        color: "white",
                        letterSpacing: "2px",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                      }}
                    >
                      {t("common.service_label")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        position: "absolute",
                        bottom: 52,
                        left: 16,
                        right: 16,
                        color: "white",
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        lineHeight: 1.35,
                      }}
                    >
                      {service[titleKey]}
                    </Typography>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 18,
                        left: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: "0.75rem",
                        }}
                      >
                        {t("common.click_to_view")}
                      </Typography>
                      <NearMeIcon
                        sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}
                      />
                    </Box>
                  </Box>

                  {/* Blur overlay (visible on hover) */}
                  <Box
                    className="card-blur-overlay"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backdropFilter: "blur(8px)",
                      bgcolor: "rgba(0,0,0,0.55)",
                      zIndex: 3,
                      opacity: 0,
                      transition: "opacity 0.35s ease",
                    }}
                  />

                  {/* Hover info (visible on hover) */}
                  <Box
                    className="card-hover-info"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 4,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      px: 3,
                      py: 4,
                      opacity: 0,
                      transform: "translateY(12px)",
                      transition: "opacity 0.35s ease, transform 0.35s ease",
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        letterSpacing: "2px",
                        fontSize: "0.65rem",
                        mb: 1.5,
                      }}
                    >
                      {t("common.service_label")}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        mb: 2,
                        lineHeight: 1.3,
                      }}
                    >
                      {service[titleKey]}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.75)",
                        lineHeight: 1.7,
                        mb: 3,
                        fontSize: "0.9rem",
                      }}
                    >
                      {service[descKey]}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: "white",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.8rem",
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("common.learn_more")}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Dot indicators */}
          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 4 }}
          >
            {services.map((_, i) => (
              <Box
                key={i}
                onClick={() =>
                  setSliderIndex(
                    Math.min(i, Math.max(0, services.length - CARDS_VISIBLE)),
                  )
                }
                sx={{
                  width: i === sliderIndex ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: i === sliderIndex ? "#1737bc" : "rgba(0,0,0,0.18)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.4)" },
                }}
              />
            ))}
          </Box>
        </Box>{" "}
        {/* end position:relative wrapper */}
      </Box>

      {/* Services → Process */}
      <Box
        sx={{
          height: 120,
          background: "linear-gradient(to bottom, #f5f5f5, #ffffff)",
          flexShrink: 0,
        }}
      />

      {/* Verfahren / Process Section */}
      <Box sx={{ bgcolor: "#ffffff", pb: { xs: 12, md: 18 } }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 8, md: 12 } }}>
            <MotionBox
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: false }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "#1737bc",
                  letterSpacing: "3px",
                  mb: 2,
                  display: "block",
                  fontSize: "0.75rem",
                }}
              >
                {t("contact.process_title")}
              </Typography>
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: false }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-1px",
                  lineHeight: 1.15,
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  mb: 2,
                }}
              >
                {t("home.process_title")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  maxWidth: 520,
                  mx: "auto",
                  fontSize: "1rem",
                  lineHeight: 1.7,
                }}
              >
                {t("home.process_subtitle")}
              </Typography>
            </MotionBox>
          </Box>

          {/* 4 Step Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr 1fr",
              },
              gap: { xs: 6, md: 5 },
            }}
          >
            {[
              {
                num: "01",
                title: t("contact.step1_title"),
                desc: t("contact.step1_desc"),
                image:
                  "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=700&q=80",
              },
              {
                num: "02",
                title: t("contact.step2_title"),
                desc: t("contact.step2_desc"),
                image:
                  "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=700&q=80",
              },
              {
                num: "03",
                title: t("contact.step3_title"),
                desc: t("contact.step3_desc"),
                image:
                  "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=700&q=80",
              },
              {
                num: "04",
                title: t("contact.step4_title"),
                desc: t("contact.step4_desc"),
                image:
                  "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=700&q=80",
              },
            ].map((step, i) => (
              <MotionBox
                key={step.num}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                viewport={{ once: false }}
              >
                {/* Image */}
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    mb: 4,
                    aspectRatio: "4/3",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    src={step.image}
                    alt={step.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 0.5s ease",
                      "&:hover": { transform: "scale(1.04)" },
                    }}
                  />
                  {/* Step badge */}
                  <MotionBox
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{
                      duration: 0.4,
                      delay: 0.3 + i * 0.15,
                      type: "spring",
                      stiffness: 200,
                    }}
                    viewport={{ once: false }}
                    sx={{
                      position: "absolute",
                      top: 20,
                      left: 20,
                      bgcolor: "#1737bc",
                      color: "white",
                      borderRadius: "50%",
                      width: 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "0.85rem",
                      letterSpacing: "0.5px",
                      boxShadow: "0 4px 16px rgba(23,55,188,0.35)",
                    }}
                  >
                    {step.num}
                  </MotionBox>
                </Box>

                {/* Text */}
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, mb: 1.5, letterSpacing: "-0.3px" }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.75, fontSize: "0.95rem" }}
                >
                  {step.desc}
                </Typography>
              </MotionBox>
            ))}
          </Box>

          {/* CTA */}
          <MotionBox
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            viewport={{ once: false }}
            sx={{ textAlign: "center", mt: { xs: 8, md: 12 } }}
          >
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: "#1737bc",
                color: "white",
                px: 5,
                py: 1.75,
                fontSize: "1rem",
                borderRadius: 1.5,
                "&:hover": {
                  bgcolor: "#0f2899",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 28px rgba(23,55,188,0.3)",
                },
                transition: "all 0.25s ease",
              }}
            >
              {t("footer.consultation_cta")}
            </Button>
          </MotionBox>
        </Container>
      </Box>

      {/* Customers Auto-Scroll Section */}
      <Box sx={{ bgcolor: "#f8f8f8", py: 6, overflow: "hidden" }}>
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: false }}
            sx={{ textAlign: "center" }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#1737bc",
                letterSpacing: "3px",
                display: "block",
                fontSize: "0.72rem",
              }}
            >
              Unsere Kunden
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mt: 1,
                letterSpacing: "-0.3px",
                color: "#1a2640",
              }}
            >
              Vertrauen, das zählt
            </Typography>
          </MotionBox>
        </Container>

        {/* Infinite scroll track */}
        <Box sx={{ position: "relative", mt: 2 }}>
          {/* Fade edges */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 120,
              background: "linear-gradient(to right, #f8f8f8, transparent)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: 120,
              background: "linear-gradient(to left, #f8f8f8, transparent)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 3,
              "@keyframes scrollLeft": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-50%)" },
              },
              animation: "scrollLeft 26s linear infinite",
              width: "max-content",
              "&:hover": { animationPlayState: "paused" },
            }}
          >
            {/* Render twice for seamless loop */}
            {[...Array(2)].map((_, repeat) =>
              customers.map((customer) => (
                <Box
                  key={`${repeat}-${customer.id}`}
                  component="a"
                  href={customer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    minWidth: 260,
                    borderRadius: 4,
                    px: 3.5,
                    py: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2.25,
                    bgcolor: "#fff",
                    flexShrink: 0,
                    textDecoration: "none",
                    border: "none",
                    boxShadow: "0 10px 28px rgba(15, 35, 95, 0.12)",
                    transition: "box-shadow 0.3s ease, transform 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 18px 38px rgba(15, 35, 95, 0.18)",
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  {/* Logo with initials fallback */}
                  <CustomerLogo
                    logo={customer.logo ?? ""}
                    initials={customer.initials ?? ""}
                    color={customer.color}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.3,
                        color: "#1a2640",
                      }}
                    >
                      {customer.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "0.76rem" }}
                    >
                      {customer.category}
                    </Typography>
                  </Box>
                </Box>
              )),
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HomePage;
